import { MobXProviderContext } from 'mobx-react';
import type { ConversationModel } from 'Models/ConversationModel';
import { useDeleteConversation } from 'Modules/conversation/conversations-list/requests/index.requests';
import { useConfirmationModalStore } from 'Modules/ui/modals/store';
import React, { useCallback } from 'react';
import { RootStoreProps } from 'Stores/RootStore.types';
import { FALLBACK_CONTACT, FALLBACK_CONVERSATION } from '../constants';
import { resolvePersonContactName, resolvePhoneContactName } from '../resolver';
import {
  getConversationMetadata,
  getDeleteConversationModalContent,
} from '../utils';

interface UseDeleteConversationModalProps {
  conversation: ConversationModel;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  onClose?: () => void;
}

/**
 * Determines the appropriate contact name to display for a conversation
 *
 * @param conversation - The conversation model containing participant information
 * @param personStore - Store containing person-related data and operations
 * @param contactStore - Store containing contact-related data and operations
 * @returns The resolved contact name or fallback value
 */
const getProperContactName = (
  conversation: ConversationModel,
  personStore: RootStoreProps['person'],
  contactStore: RootStoreProps['contact'],
  conversationStore: RootStoreProps['conversation']
): string => {
  if (conversation.grouping !== 'OneOnOne') {
    if (
      conversation.grouping === 'Group' &&
      conversation.topic?.startsWith('Group--')
    ) {
      return conversationStore.createProperName(
        conversation.participants.filter(
          (participant) => participant.personId !== personStore.loggedInPersonId
        )
      );
    }

    return conversation.topic || FALLBACK_CONVERSATION;
  }

  const otherPtc = conversation.participants.find(
    ({ personId }) => personId !== personStore.loggedInPersonId
  );

  if (!otherPtc) {
    return conversation.topic || FALLBACK_CONTACT;
  }

  // Handle phone-based contacts (SMS)
  if (otherPtc.phone) {
    return resolvePhoneContactName(otherPtc, contactStore, personStore);
  }

  // Handle person-based contacts
  if (otherPtc.personId && otherPtc.personId > 0) {
    return resolvePersonContactName(otherPtc, personStore, conversation.topic);
  }

  return conversation.topic || FALLBACK_CONTACT;
};

/**
 * Custom hook for handling delete conversation modal and action
 * @param props - Hook configuration including conversation and callbacks
 * @returns Object with handleDeleteConversation function
 */
export const useDeleteConversationModal = ({
  conversation,
  onSuccess,
  onError,
  onClose,
}: UseDeleteConversationModalProps) => {
  const { setOpenConfirmationModal } = useConfirmationModalStore();
  const { mutate: deleteConversation } = useDeleteConversation();

  const {
    person: personStore,
    contact: contactStore,
    conversation: conversationStore,
  } = React.useContext<RootStoreProps>(MobXProviderContext);

  const handleDeleteConversation = useCallback(
    (e?: React.MouseEvent) => {
      // Stop event propagation to prevent conversation selection
      e?.stopPropagation();

      // Get the proper contact name
      const properContactName = getProperContactName(
        conversation,
        personStore,
        contactStore,
        conversationStore
      );

      // Get conversation metadata
      const metadata = getConversationMetadata(conversation, properContactName);

      // Get modal content
      const modalContent = getDeleteConversationModalContent(metadata);

      // Close any open popovers
      onClose?.();

      // Open confirmation modal
      setOpenConfirmationModal({
        title: modalContent.title,
        description: modalContent.description,
        testid: 'deleteConversationModal',
        textCancel: 'Cancel',
        textConfirm: modalContent.confirmText,
        onConfirm: () => {
          deleteConversation(
            {
              conversationId: conversation.id,
              conversationType: metadata.conversationType,
              isActiveGroup: metadata.isActiveGroup,
            },
            {
              onSuccess: () => {
                // Call the onSuccess callback if provided
                onSuccess?.();
              },
              onError: (error) => {
                // Call the onError callback if provided
                onError?.(error);
              },
            }
          );
        },
        onCancel: () => {
          // No need to call onClose here as modal is already closed
        },
      });
    },
    [
      conversation,
      onClose,
      setOpenConfirmationModal,
      deleteConversation,
      personStore,
      contactStore,
      conversationStore,
      onSuccess,
      onError,
    ]
  );

  return {
    handleDeleteConversation,
  };
};
